/**
 * THE INVISIBLE TEACHER - v2.0
 * =====================
 * USER-INITIATED STATE MACHINE (Push-to-Talk Protocol)
 *
 * STATES:
 * - IDLE: Not active. Show "Start Lesson" button.
 * - READING: TTS playing. Mic DISABLED. Show [Stop Reading].
 * - PAUSED: Section done. Show [Continue], [Repeat], [Explain], [Ask Question].
 * - LISTENING: Mic active (push-to-talk via [Ask Question]). Recording indicator shown.
 * - EXPLAINING: Playing explanation audio. Mic DISABLED. Show [Stop Reading].
 *
 * RULES:
 * 1. Mic NEVER listens by default (no "Always-Listening")
 * 2. Mic only activates when user clicks [Ask Question]
 * 3. Automatic cutoff when user stops speaking
 * 4. MUTE-ON-SPEAK: Mic programmatically disabled when TTS is active
 * 5. [Stop Reading] kills window.speechSynthesis + backend TTS queue immediately
 * 6. STT failure shows visual prompt, never "airs out"
 */

import { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { isYoungLearnerGrade } from "../utils/gradeUtils";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Teaching states
const STATE = {
  IDLE: "idle",
  READING: "reading",
  PAUSED: "paused",
  LISTENING: "listening",
  EXPLAINING: "explaining",
};

// Global audio lock - prevents multiple voices playing simultaneously
let globalAudioLock = false;
let globalAudioInstance = null;
let globalComponentId = null;

// Voice maturity mapping
const VOICES = {
  "K-2": "shimmer",
  "3-5": "nova",
  "6-8": "echo",
  "9-12": "coral",
  College: "onyx",
  Master: "sage",
  Ordination: "fable",
};

const getVoice = (gradeLevel) => {
  if (!gradeLevel) return "echo";
  const level = gradeLevel.toUpperCase();
  if (level.includes("K") || level.includes("1") || level.includes("2")) return "shimmer";
  if (level.includes("3") || level.includes("4") || level.includes("5")) return "nova";
  if (level.includes("6") || level.includes("7") || level.includes("8")) return "echo";
  if (level.includes("9") || level.includes("10") || level.includes("11") || level.includes("12")) return "coral";
  if (level.includes("COLLEGE")) return "onyx";
  if (level.includes("MASTER")) return "sage";
  if (level.includes("ORDINATION")) return "fable";
  return "echo";
};

// Compact, solid-bg secondary pill style for the control bar. Solid dark
// background ensures the pill does NOT bleed visually over the lesson
// text behind the floating panel (the previous translucent versions were
// confusing on mobile, see screenshots 2026-05-31).
const pillSecondary = (accent) => ({
  background: "rgba(255,255,255,0.06)",
  color: accent,
  border: `1px solid ${accent}80`,
  borderRadius: "999px",
  padding: "8px 11px",
  fontSize: "0.78rem",
  fontWeight: 600,
  cursor: "pointer",
  whiteSpace: "nowrap",
  flexShrink: 0,
  lineHeight: 1,
});

export default function InvisibleTeacher({
  gradeLevel = "6-8",
  lessonContent = "",
  lessonTitle = "",
  user,
  isExamMode = false,
  onTeachingStart,
  onTeachingEnd,
  onSectionComplete,
  // Fired the moment audio for a section STARTS playing — used by
  // host components (Living School LessonViewer, K-12 LessonWorkspace)
  // to keep the visible paragraph counter and rendered text in lock
  // step with what the AI is currently reading aloud. User mandate
  // 2026-02-11c: "place safeguards so whatever is being read can also
  // be seen by the users — no student left confused."
  onSectionStart,
  onLessonComplete,
  autoStart = false,
}) {
  // Core state machine
  const [teachingState, setTeachingState] = useState(STATE.IDLE);
  const [currentSection, setCurrentSection] = useState(0);
  const [sections, setSections] = useState([]);
  const [statusMessage, setStatusMessage] = useState("");
  const [error, setError] = useState("");

  // Explain & highlight state
  const [highlightedText, setHighlightedText] = useState("");
  const [explanationText, setExplanationText] = useState("");
  const [citations, setCitations] = useState([]);  // web-search sources for Q&A
  const [expandedCitations, setExpandedCitations] = useState(new Set());  // Set<citation.id> currently open
  const [sttError, setSttError] = useState(false);
  const [interimTranscript, setInterimTranscript] = useState("");
  const [finalTranscript, setFinalTranscript] = useState("");
  const [showTextInput, setShowTextInput] = useState(false);
  const [textQuestion, setTextQuestion] = useState("");
  // 2026-04-25: Voice-Input requirement — the [input][mic][send] panel must
  // stay open for the user to review/edit transcribed speech BEFORE sending.
  // We track this independently of `isListening` so stopping the mic doesn't
  // hide the box.
  const [questionPanelOpen, setQuestionPanelOpen] = useState(false);

  // Refs
  const audioRef = useRef(null);
  const recognitionRef = useRef(null);
  const sectionsRef = useRef([]);
  const teachingStateRef = useRef(STATE.IDLE);
  const componentIdRef = useRef(`it-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`);
  const silenceTimerRef = useRef(null);
  const finalTranscriptRef = useRef("");
  const textAtRecordingStartRef = useRef("");  // snapshot of typed text before mic on
  const latestQuestionTextRef = useRef("");
  const listenRestartsRef = useRef(0);
  const autoContinueTimerRef = useRef(null);  // K-2 auto-continue setTimeout
  const autoContinueIntervalRef = useRef(null); // 100ms ticker for visible countdown

  // 3-second auto-advance countdown displayed in the PAUSED state.
  // null = no countdown active. 3000 → 0 ms remaining.
  const [autoContinueMs, setAutoContinueMs] = useState(null);

  // ── Auto-continue cancellation helper ────────────────────────────
  // Called by every button handler (Continue / Repeat / Skip / Explain
  // / Ask / Stop) and by stopTeaching/unmount so the 3-second timer
  // never fires after the student has moved on.
  const cancelAutoContinue = () => {
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    if (autoContinueIntervalRef.current) {
      clearInterval(autoContinueIntervalRef.current);
      autoContinueIntervalRef.current = null;
    }
    setAutoContinueMs(null);
  };

  // ─── Prefetch ─────────────────────────────────────────────────
  // While section N is playing, we kick off TTS generation for section N+1
  // in the background. When the user (or auto-continue) advances, the audio
  // is already in-hand — no spinner, no dead air. Cuts wait time per section
  // from ~3-6s down to ~50ms.
  const prefetchRef = useRef({ index: -1, audioUrl: null, promise: null });

  // ─── Persistence ───────────────────────────────────────────────
  // Resume from the last section the student stopped at, keyed by lesson
  // title. Cleared once the lesson is fully completed.
  const lessonKey = (lessonTitle || "").trim().toLowerCase().replace(/\s+/g, "_");
  const progressStorageKey = lessonKey ? `it_lesson_pos:${lessonKey}` : null;

  // FIXED 2026-04-25: was using substring `.includes("1")` / `.includes("2")`
  // which falsely matched Grades 11, 12, 9-12 → triggered 8s auto-continue loop.
  const isYoungLearner = isYoungLearnerGrade(gradeLevel);

  // Keep ref in sync with state for async callbacks
  useEffect(() => {
    teachingStateRef.current = teachingState;
  }, [teachingState]);

  // Parse content into sections.
  // SAFETY: when `lessonContent` changes mid-teaching (user navigates between
  // Living-School events, or the parent re-fetches), kill any in-flight audio
  // and reset to IDLE so we don't get stale callbacks driving into the new
  // section list (which previously caused a "looping" feel).
  //
  // 2026-02-11d UNIVERSAL READING SAFEGUARD:
  // The 9th-grade Algebra 1 lesson failed silently because the previous
  // `length > 10` filter dropped short paragraphs (equations like
  // "x + 5 = 12", definition lines like "Definition: Variable"). With
  // most paragraphs filtered out, the AI either had nothing to read
  // or skipped large chunks. We now keep ANY paragraph with at least
  // 3 non-whitespace characters and only drop truly empty entries.
  useEffect(() => {
    if (!lessonContent) {
      sectionsRef.current = [];
      setSections([]);
      return;
    }
    const parsed = lessonContent
      .split(/\n\n+/)
      .map((p) => p.trim())
      .filter((p) => p.length >= 3);
    // If the parsed text is identical to what we already have, skip — this
    // prevents resets when a parent component re-renders with the same string.
    const prev = sectionsRef.current;
    if (
      prev.length === parsed.length &&
      prev.every((s, i) => s === parsed[i])
    ) {
      return;
    }
    // Real content change → hard-stop any in-flight audio/state.
    try {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
      if ("speechSynthesis" in window) window.speechSynthesis.cancel();
      if (globalComponentId === componentIdRef.current) {
        globalAudioInstance = null;
        globalAudioLock = false;
      }
    } catch {
      /* ignore */
    }
    sectionsRef.current = parsed;
    setSections(parsed);
    // Restore saved progress so the student can resume where they stopped.
    // We clamp to valid bounds and clear the saved value if it points past
    // the new section list.
    let resumeIdx = 0;
    if (progressStorageKey) {
      try {
        const saved = parseInt(localStorage.getItem(progressStorageKey), 10);
        if (!isNaN(saved) && saved > 0 && saved < parsed.length) {
          resumeIdx = saved;
        }
      } catch {
        /* ignore */
      }
    }
    setCurrentSection(resumeIdx);
    setTeachingState(STATE.IDLE);
    teachingStateRef.current = STATE.IDLE;
    setHighlightedText("");
    setExplanationText("");
    setStatusMessage(resumeIdx > 0 ? `Ready to resume at section ${resumeIdx + 1}` : "");
    // Reset prefetch — old audio URLs are from a different lesson
    prefetchRef.current = { index: -1, audioUrl: null, promise: null };

    // ── 2026-02-16d INSTANT-START PRE-WARM ──────────────────────────
    // The user reported "It takes a long time for the invisible teacher
    // to start reading sections" on production. Root cause: when the
    // student taps Play on section 0, the full TTS round-trip
    // (OpenAI cold-start + bytes + audio buffering, often 3-6s) is
    // serialized in front of the first audible sound. We now warm
    // section 0's audio the moment the lesson content arrives, while
    // the student is still reading the intro screen. By the time they
    // click Play, the audio is usually already cached and starts
    // within ~200ms instead of 3-6s. The prefetch is best-effort and
    // silently swallows errors so it never blocks the UI.
    if (parsed.length > 0 && resumeIdx < parsed.length) {
      const firstText = parsed[resumeIdx];
      const promise = generateAudio(firstText);
      prefetchRef.current = { index: resumeIdx, audioUrl: null, promise };
      promise
        .then((url) => {
          if (prefetchRef.current.index === resumeIdx) {
            prefetchRef.current.audioUrl = url;
          }
        })
        .catch(() => {
          /* non-fatal — generateAudio retries internally, then falls
             back to browser TTS in readSection */
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lessonContent]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      killAllAudio();
      stopMicrophone();
      if (globalComponentId === componentIdRef.current) {
        globalAudioLock = false;
        globalComponentId = null;
        globalAudioInstance = null;
      }
    };
  }, []);

  // ── Global "restart teacher" listener ──────────────────────────
  // For consumers like Living School (EventTriggeredCurriculum)
  // where the InvisibleTeacher is rendered as a sibling of the
  // lesson body and cannot be remounted via React `key`. Any view
  // can `window.dispatchEvent(new CustomEvent("ylc:teacher-restart"))`
  // to force the teacher back to section 0 and stop any audio in
  // progress — matching the "Restart Lesson" UX of every other
  // track on the platform.
  useEffect(() => {
    const handler = () => {
      try {
        killAllAudio();
        if (progressStorageKey) localStorage.removeItem(progressStorageKey);
      } catch {
        /* ignore */
      }
      setCurrentSection(0);
      setTeachingState(STATE.IDLE);
      teachingStateRef.current = STATE.IDLE;
      setHighlightedText("");
      setExplanationText("");
      setStatusMessage("");
      prefetchRef.current = { index: -1, audioUrl: null, promise: null };
    };
    window.addEventListener("ylc:teacher-restart", handler);
    return () => window.removeEventListener("ylc:teacher-restart", handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [progressStorageKey]);

  // ═══════════════════════════════════════════════════════════════
  // AUDIO ENGINE
  // ═══════════════════════════════════════════════════════════════

  const killAllAudio = useCallback(() => {
    // Kill backend TTS audio
    if (audioRef.current) {
      try {
        audioRef.current.pause();
        audioRef.current.currentTime = 0;
      } catch (e) {}
      audioRef.current = null;
    }
    // Kill browser TTS
    if ("speechSynthesis" in window) {
      window.speechSynthesis.cancel();
    }
    // Release global audio instance
    if (globalComponentId === componentIdRef.current && globalAudioInstance) {
      try {
        globalAudioInstance.pause();
      } catch (e) {}
      globalAudioInstance = null;
    }
    // Cancel any pending K-2 auto-continue timer so it can't fire after stop
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
  }, []);

  const generateAudio = async (text) => {
    // 2026-02-11d READING SAFEGUARD — defensive retry.
    // Algebra 1 (and other text-heavy lessons) intermittently saw the
    // TTS request fail on first try due to backend cold-start. The
    // previous implementation returned null on the first failure,
    // silently skipping that section's audio. We now retry once with
    // a 500ms backoff before giving up so the AI doesn't "skip" a
    // section the student needed to hear.
    const safeText = (text || "").substring(0, 4000);
    if (!safeText.trim()) return null;
    const token = localStorage.getItem("token");
    if (!token) return null;

    const attempt = async () => {
      const response = await axios.post(
        `${API}/classroom/voice-first/read`,
        {
          content_text: safeText,
          grade_level: gradeLevel,
          is_examination: isExamMode,
          section_index: currentSection,
          total_sections: sections.length,
          // 2026-02-21: hand the backend the lesson title so it can
          // auto-detect math content and slow the TTS down. Title is
          // the most reliable hint we have ("Grade 5 Math: Fractions").
          lesson_title: lessonTitle || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );
      if (response.data?.fallback) return null;
      return response.data?.audio_url || null;
    };

    try {
      const first = await attempt();
      if (first) return first;
    } catch (err) {
      console.warn("TTS first attempt failed, retrying:", err?.message || err);
    }
    await new Promise((r) => setTimeout(r, 500));
    try {
      return await attempt();
    } catch (err) {
      console.error("TTS retry failed — falling back to browser speech:", err?.message || err);
      return null;
    }
  };

  const playAudio = (text, audioUrl) => {
    // Acquire global lock
    globalAudioLock = true;
    globalComponentId = componentIdRef.current;
    if (globalAudioInstance) {
      try {
        globalAudioInstance.pause();
      } catch (e) {}
    }

    return new Promise((resolve) => {
      // Guard against double-fallback: when `audio.play()` fails, both
      // `audio.onerror` AND `audio.play().catch()` can fire, each kicking off
      // a separate `playBrowserTTS` → section gets read TWICE back-to-back.
      // This is the main "looping" symptom users experience on grades > 2.
      let settled = false;
      const settleWithFallback = () => {
        if (settled) return;
        settled = true;
        globalAudioInstance = null;
        playBrowserTTS(text).then(() => {
          if (!resolvedOnce) {
            resolvedOnce = true;
            resolve();
          }
        });
      };
      let resolvedOnce = false;
      const settleOk = () => {
        if (settled) return;
        settled = true;
        globalAudioInstance = null;
        if (!resolvedOnce) {
          resolvedOnce = true;
          resolve();
        }
      };

      try {
        if (audioUrl) {
          const audio = new Audio(audioUrl);
          audioRef.current = audio;
          globalAudioInstance = audio;
          audio.onended = settleOk;
          audio.onerror = settleWithFallback;
          // 2026-02-16d — flip "Preparing voice…" to "Reading…" the
          // moment audio actually begins. Gives the student crisp
          // feedback that the wait is over.
          audio.onplaying = () => {
            try {
              setStatusMessage((cur) =>
                typeof cur === "string" && cur.startsWith("Preparing voice")
                  ? cur.replace("Preparing voice", "Reading")
                  : cur
              );
            } catch {
              /* non-fatal */
            }
          };
          audio.play().catch(settleWithFallback);
        } else {
          settled = true; // no primary audio — go straight to browser TTS
          playBrowserTTS(text).then(() => {
            if (!resolvedOnce) {
              resolvedOnce = true;
              resolve();
            }
          });
        }
      } catch (e) {
        console.warn("Audio error:", e);
        if (!resolvedOnce) {
          resolvedOnce = true;
          resolve();
        }
      }
    });
  };

  const playBrowserTTS = (text) => {
    return new Promise((resolve) => {
      if (!("speechSynthesis" in window)) {
        resolve();
        return;
      }
      try {
        window.speechSynthesis.cancel();
        const utterance = new SpeechSynthesisUtterance(text);
        utterance.rate = isYoungLearner ? 0.85 : 1.0;
        utterance.pitch = 1.0;
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          const preferred = voices.find((v) => v.lang.startsWith("en")) || voices[0];
          utterance.voice = preferred;
        }
        utterance.onend = () => resolve();
        utterance.onerror = () => resolve();
        setTimeout(() => {
          try {
            window.speechSynthesis.speak(utterance);
          } catch (e) {
            resolve();
          }
        }, 100);
      } catch (e) {
        resolve();
      }
    });
  };

  // ═══════════════════════════════════════════════════════════════
  // MICROPHONE ENGINE — Industry-Standard Speech Recognition
  // ═══════════════════════════════════════════════════════════════
  // continuous mode + real-time interim transcript + silence timeout
  // + text input fallback when STT unavailable or fails

  const startMicrophone = async () => {
    // MUTE-ON-SPEAK: Block mic if audio is playing
    if (
      teachingStateRef.current === STATE.READING ||
      teachingStateRef.current === STATE.EXPLAINING
    ) {
      setError("Wait until the reading finishes before asking a question.");
      return;
    }

    // Check for SpeechRecognition support first.
    // Production note: Web Speech API is supported on Chrome/Edge (desktop+
    // Android), Safari ≥14.1 (iOS+Mac). It is NOT supported on Firefox, on
    // some embedded webviews (e.g., Facebook in-app browser), nor on iOS
    // when the page is opened from a non-HTTPS origin. We surface a clear
    // user-visible reason instead of silently dropping the click.
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SpeechRecognition) {
      setShowTextInput(true);
      setError(
        "Voice input isn't supported on this browser. " +
        "Open the site in Chrome, Edge, or Safari and try again — or type your question below."
      );
      setStatusMessage("Type your question and tap Send.");
      return;
    }

    // Request mic permission. On production (ylcenter.com), users hitting
    // the page for the first time MUST be on HTTPS (https://ylcenter.com),
    // not an embedded webview, and must approve the browser prompt. We
    // surface the exact denial reason so the user knows what to fix.
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      // Release the stream immediately — SpeechRecognition uses its own
      // capture pipeline; we only requested permission to make the prompt
      // appear early so the user knows it's required.
      stream.getTracks().forEach((t) => { try { t.stop(); } catch {} });
    } catch (e) {
      const reason =
        e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError"
          ? "Microphone permission was denied for this site. Open the address-bar lock icon → Site settings → Microphone → Allow, then reload."
          : e?.name === "NotFoundError"
          ? "No microphone detected on this device. You can still type your question."
          : e?.name === "NotSecureError" || window.location.protocol !== "https:"
          ? "Microphone requires HTTPS. Make sure you're on https://ylcenter.com (not a webview)."
          : `Microphone unavailable (${e?.name || "unknown"}). You can still type your question.`;
      setShowTextInput(true);
      setError(reason);
      setStatusMessage("Type your question and tap Send.");
      return;
    }

    const recognition = new SpeechRecognition();
    recognition.continuous = true;        // Keep listening until we stop
    recognition.interimResults = true;    // Show real-time what user is saying
    recognition.lang = "en-US";
    recognition.maxAlternatives = 1;

    // 2026-04-25 USER REQUIREMENT: Voice Input must stream directly INTO the
    // editable text box (not into a separate read-only transcript), and the
    // message MUST NOT auto-send. We snapshot whatever text was already typed
    // before recording started so the user's edits aren't wiped by appended
    // speech, then append `final + interim` on every recognition event.
    finalTranscriptRef.current = "";
    setInterimTranscript("");
    setFinalTranscript("");
    textAtRecordingStartRef.current = textQuestion;  // preserve manual edits
    latestQuestionTextRef.current = textQuestion;
    listenRestartsRef.current = 0;

    recognition.onresult = (event) => {
      // ── iter 72 — scrambled transcript fix ──────────────────
      // The previous handler read event.results starting at
      // `event.resultIndex` and APPENDED any `isFinal` segments to a
      // running ref. On browsers that re-fire the same finalized
      // segment across events (Chrome/Edge do this when the engine
      // "finalizes" an in-flight phrase as the user keeps speaking),
      // the same text got appended multiple times — producing
      // scrambled, duplicated transcripts like
      // "What is Shabbat What is Shabbat What is Shabbat ...".
      // The fix is to rebuild the final/interim snapshots from
      // scratch on every event by iterating ALL results and using
      // `isFinal`, then REPLACE (not append) the ref. This is the
      // deduplicated truth per the Web Speech API spec.
      // Android Chrome re-sends CUMULATIVE text per result ("what", "what is",
      // "what is the sabbath"), desktop sends distinct segments. Merge so
      // a segment that extends/repeats the text so far replaces instead of appends.
      const mergeSegment = (acc, seg) => {
        const a = acc.toLowerCase();
        const b = seg.toLowerCase();
        if (!seg) return acc;
        if (!acc) return seg;
        if (b.startsWith(a)) return seg;
        if (a.endsWith(b)) return acc;
        return `${acc} ${seg}`;
      };
      let interim = "";
      let final = "";
      for (let i = 0; i < event.results.length; i++) {
        const t = (event.results[i][0]?.transcript || "").replace(/\s+/g, " ").trim();
        if (event.results[i].isFinal) {
          final = mergeSegment(final, t);
        } else {
          interim = mergeSegment(interim, t);
        }
      }
      if (interim && final && interim.toLowerCase().startsWith(final.toLowerCase())) {
        interim = interim.slice(final.length).trim();
      }
      finalTranscriptRef.current = final;
      setFinalTranscript(final);
      setInterimTranscript(interim);

      // Live-merge into the editable text box. Format:
      //   [user-typed text] [recognized final] [recognized interim (italic preview)]
      // The interim portion is kept inside textQuestion too so the box shows
      // real-time streaming; once final, it stabilizes.
      const prefix = textAtRecordingStartRef.current
        ? textAtRecordingStartRef.current.trimEnd() + " "
        : "";
      const merged = (prefix + final + " " + interim)
        .replace(/\s+/g, " ")
        .trim();
      latestQuestionTextRef.current = merged;
      setTextQuestion(merged);
    };

    recognition.onerror = (event) => {
      if (event.error === "not-allowed" || event.error === "service-not-allowed") {
        recognitionRef.current = null; // never auto-restart without permission
      }
      // Errors no longer force a UI mode-switch — the text box was always
      // visible, so the user can simply type. We just surface a small status.
      if (event.error === "no-speech") {
        setStatusMessage("No speech detected. You can type your question and tap Send.");
      } else if (event.error === "not-allowed") {
        setStatusMessage("Microphone permission denied. You can still type your question.");
      } else if (event.error !== "aborted") {
        setStatusMessage("Speech error. You can type your question.");
      }
    };

    recognition.onend = () => {
      // Browsers end recognition on their own after silence or ~60 s. If the
      // student never tapped stop, keep actively listening and keep the text.
      if (
        recognitionRef.current === recognition &&
        teachingStateRef.current === STATE.LISTENING &&
        listenRestartsRef.current < 30
      ) {
        listenRestartsRef.current += 1;
        textAtRecordingStartRef.current = latestQuestionTextRef.current;
        finalTranscriptRef.current = "";
        try {
          recognition.start();
          return;
        } catch (e) { /* fall through to paused state */ }
      }
      // CRUCIAL: do NOT auto-submit. The user must explicitly click Send.
      // We simply transition out of LISTENING back into PAUSED so the mic
      // button switches its icon, and we keep the (now editable) text in the
      // input box for the user to review / edit.
      clearSilenceTimer();
      recognitionRef.current = null;
      if (teachingStateRef.current === STATE.LISTENING) {
        setTeachingState(STATE.PAUSED);
        setStatusMessage(
          latestQuestionTextRef.current.trim()
            ? "Review your question, then tap Send."
            : "Tap the mic again to speak, or type your question."
        );
      }
      setInterimTranscript("");
    };

    recognitionRef.current = recognition;

    try {
      recognition.start();
      setTeachingState(STATE.LISTENING);
      setSttError(false);
      // No silence timer — mic stays on until the user explicitly clicks
      // the mic button again. This honors the requirement that the system
      // never auto-stops or auto-sends.
      setStatusMessage("Listening… speak naturally. Tap the mic again to stop.");
    } catch (e) {
      setStatusMessage("Could not start mic. Type your question and tap Send.");
    }
  };

  const startSilenceTimer = (ms = 2500) => {
    clearSilenceTimer();
    silenceTimerRef.current = setTimeout(() => {
      // Silence detected — stop recognition, trigger processing
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
    }, ms);
  };

  const clearSilenceTimer = () => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  };

  const stopMicrophone = () => {
    clearSilenceTimer();
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
    }
  };

  const processQuestion = (question) => {
    stopMicrophone();
    setInterimTranscript("");
    setShowTextInput(false);
    if (question && question.trim()) {
      handleStudentQuestion(question.trim());
    } else {
      showSttError();
    }
  };

  // Manual send — user clicks the Send button. We only ever submit from here;
  // the speech-recognition stream NEVER auto-submits, so the user can edit the
  // transcribed text before sending.
  const handleManualSend = () => {
    const question = textQuestion.trim();
    if (!question) return;
    // Stop any active recording before sending.
    if (recognitionRef.current) {
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
    }
    setTextQuestion("");
    finalTranscriptRef.current = "";
    setFinalTranscript("");
    setInterimTranscript("");
    textAtRecordingStartRef.current = "";
    setQuestionPanelOpen(false);
    setCitations([]);
    processQuestion(question);
  };

  const handleTextSubmit = (e) => {
    e.preventDefault();
    handleManualSend();
  };

  // Toggle the microphone: start recording if idle, stop if recording.
  // Idempotent and safe to spam-click.
  const toggleMicrophone = () => {
    if (recognitionRef.current) {
      // Stop — keeps whatever text is in the box for review/edit.
      try { recognitionRef.current.stop(); } catch (e) {}
      recognitionRef.current = null;
      clearSilenceTimer();
      if (teachingStateRef.current === STATE.LISTENING) {
        setTeachingState(STATE.PAUSED);
      }
      setStatusMessage(
        latestQuestionTextRef.current.trim()
          ? "Review your question, then tap Send."
          : "Tap the mic again to speak, or type your question."
      );
      setInterimTranscript("");
    } else {
      // Start fresh recording. startMicrophone() handles all the wiring.
      startMicrophone();
    }
  };

  const cancelListening = () => {
    stopMicrophone();
    setInterimTranscript("");
    setFinalTranscript("");
    finalTranscriptRef.current = "";
    textAtRecordingStartRef.current = "";
    setShowTextInput(false);
    setTextQuestion("");
    setQuestionPanelOpen(false);
    setTeachingState(STATE.PAUSED);
    setStatusMessage("Ready for the next section?");
  };

  const showSttError = () => {
    setSttError(true);
    setTeachingState(STATE.PAUSED);
    setStatusMessage("I didn't quite catch that. Click the mic to try again.");
    setTimeout(() => setSttError(false), 5000);
  };

  // ═══════════════════════════════════════════════════════════════
  // TEACHING STATE MACHINE
  // ═══════════════════════════════════════════════════════════════

  // Re-entrance guard: prevents multiple startTeaching / readSection / handleContinue
  // invocations from running concurrently. A rapid double-click on Start/Continue,
  // or an orphan auto-continue timer firing alongside a manual click, can
  // otherwise spawn two parallel read-loops that race each other and sound
  // exactly like "looping" audio.
  const teachingRunIdRef = useRef(0);
  const readingInProgressRef = useRef(false);

  const startTeaching = async () => {
    if (sections.length === 0) {
      // 2026-02-11d safeguard: surface a clear, actionable error so
      // the student knows the system saw their click but couldn't
      // find anything to read (instead of silent "did I do something
      // wrong?" UI). Logs server-side context for diagnosis.
      setError(
        lessonContent
          ? "Couldn't extract teachable sections from this lesson. The text may need to be regenerated."
          : "No lesson content was loaded — please tap Restart Lesson or refresh the page."
      );
      // eslint-disable-next-line no-console
      console.warn(
        "[InvisibleTeacher] Empty sections at startTeaching():",
        { hasLessonContent: !!lessonContent, lessonTitle, gradeLevel }
      );
      return;
    }
    if (globalAudioLock && globalComponentId !== componentIdRef.current) return;
    if (readingInProgressRef.current) return; // ignore concurrent start

    // Bump the run ID — any in-flight callbacks from a previous run will see a
    // stale ID and abort (defense against lingering Promises after stop/restart).
    teachingRunIdRef.current += 1;
    const runId = teachingRunIdRef.current;

    setTeachingState(STATE.READING);
    // Resume from where the student last stopped (currentSection has already
    // been restored from localStorage in the parse-sections effect). Only
    // play the intro on a true fresh start (section 0).
    const startIdx = currentSection > 0 && currentSection < sectionsRef.current.length
      ? currentSection
      : 0;
    setCurrentSection(startIdx);
    setError("");
    setSttError(false);
    setHighlightedText("");
    setExplanationText("");
    onTeachingStart?.();

    if (startIdx === 0) {
      // Shortened intro — was ~11 words ("Shalom! Today we will learn about
      // X. Let's begin.") which took ~3-4s of TTS generation + playback. Now
      // ~5 words, ~1.5s. Combined with parallel section-0 prefetch this cuts
      // "time to first lesson section" from ~7s to ~2-3s.
      const intro = "Shalom! Let's begin.";

      setStatusMessage("Starting lesson...");
      // ── Kick off prefetch of section 0 IN PARALLEL with intro generation ──
      // Both TTS calls now race; whichever finishes first plays first.
      const firstAudioPromise = generateAudio(sectionsRef.current[0] || "");
      prefetchRef.current = { index: 0, audioUrl: null, promise: firstAudioPromise };
      firstAudioPromise.then((url) => {
        if (prefetchRef.current.index === 0) prefetchRef.current.audioUrl = url;
      }).catch(() => {});

      const introAudio = await generateAudio(intro);
      if (runId !== teachingRunIdRef.current) return; // user clicked Stop/restart
      await playAudio(intro, introAudio);
      if (runId !== teachingRunIdRef.current) return;
    } else {
      setStatusMessage(`Resuming at section ${startIdx + 1}...`);
    }

    if (teachingStateRef.current !== STATE.IDLE) {
      await readSection(startIdx);
    }
  };

  const readSection = async (sectionIndex) => {
    if (sectionIndex >= sectionsRef.current.length) {
      await finishLesson();
      return;
    }
    // Prevent two readSection calls running in parallel (e.g., user clicks
    // Continue while an auto-continue timer also fires).
    if (readingInProgressRef.current) return;
    readingInProgressRef.current = true;
    const runId = teachingRunIdRef.current;

    try {
      setCurrentSection(sectionIndex);
      // Persist progress so a refresh / re-open resumes here
      if (progressStorageKey) {
        try { localStorage.setItem(progressStorageKey, String(sectionIndex)); } catch {}
      }
      // ── Bump the global "last lesson" pointer's section + timestamp ──
      // The Dashboard ResumeLessonCard reads ylc_last_lesson; LessonWorkspace
      // seeds the metadata (subject, grade, lesson number) on mount, and we
      // just patch the moving fields here as the student advances.
      try {
        const existing = JSON.parse(localStorage.getItem("ylc_last_lesson") || "null");
        if (existing && existing.lessonKey === lessonKey) {
          localStorage.setItem("ylc_last_lesson", JSON.stringify({
            ...existing,
            sectionIndex,
            totalSections: sectionsRef.current.length,
            updatedAt: Date.now(),
          }));
        }
      } catch {}
      setTeachingState(STATE.READING);
      setHighlightedText(sectionsRef.current[sectionIndex]);
      setExplanationText("");
      // 2026-02-16d INSTANT-FEEDBACK SAFEGUARD:
      // Show a clearer status while the audio prepares. If the prefetch
      // already has audio for this section the message will flip to
      // "Reading section…" within milliseconds — but if the TTS round-trip
      // is still in flight, the student knows we're working and isn't left
      // wondering whether their tap registered.
      const hasPrefetch =
        prefetchRef.current.index === sectionIndex &&
        (prefetchRef.current.audioUrl || prefetchRef.current.promise);
      setStatusMessage(
        hasPrefetch
          ? `Reading section ${sectionIndex + 1} of ${sectionsRef.current.length}...`
          : `Preparing voice for section ${sectionIndex + 1}...`
      );

      // ── KEEP THE VISIBLE LESSON IN LOCK-STEP WITH THE AUDIO ──
      // Fire the callback BEFORE the audio actually plays so the host
      // component (LessonViewer / LessonWorkspace) can advance its
      // visible paragraph counter and scroll the matching paragraph
      // into view. Otherwise the student sees "2 / 27" on screen while
      // the AI is narrating section 15, which the user reported as
      // confusing.
      try {
        onSectionStart?.(sectionIndex);
      } catch (e) {
        console.error("onSectionStart callback failed:", e);
      }

      const sectionText = sectionsRef.current[sectionIndex];

      // ── Reuse prefetch if available, otherwise generate now ─────────
      let audioUrl;
      const pf = prefetchRef.current;
      if (pf.index === sectionIndex && (pf.audioUrl || pf.promise)) {
        audioUrl = pf.audioUrl || (await pf.promise);
      } else {
        audioUrl = await generateAudio(sectionText);
      }
      // Clear the consumed prefetch slot
      prefetchRef.current = { index: -1, audioUrl: null, promise: null };

      // Stale run (user hit stop / restarted) → abort cleanly.
      if (runId !== teachingRunIdRef.current) return;
      if (teachingStateRef.current === STATE.IDLE) return;

      // ── Kick off prefetch of the NEXT section while we play this one ──
      const nextIdx = sectionIndex + 1;
      if (nextIdx < sectionsRef.current.length) {
        const nextText = sectionsRef.current[nextIdx];
        const promise = generateAudio(nextText);
        prefetchRef.current = { index: nextIdx, audioUrl: null, promise };
        promise.then((url) => {
          // Only stash if still relevant (user might have skipped)
          if (prefetchRef.current.index === nextIdx) {
            prefetchRef.current.audioUrl = url;
          }
        }).catch(() => {});
      }

      await playAudio(sectionText, audioUrl);

      if (runId !== teachingRunIdRef.current) return;
      if (teachingStateRef.current === STATE.IDLE) return;

      onSectionComplete?.(sectionIndex);

      // Transition to PAUSED — start a 3-second auto-advance with the
      // option for the student to Repeat the section for clarity. Any
      // button click cancels the timer (see cancelAutoContinue).
      setTeachingState(STATE.PAUSED);
      setStatusMessage("Continuing in 3 seconds — tap Repeat to hear it again");

      if (sectionIndex < sectionsRef.current.length - 1) {
        cancelAutoContinue();
        // Visible countdown ticker (100ms granularity)
        const startedAt = Date.now();
        const TOTAL_MS = 3000;
        setAutoContinueMs(TOTAL_MS);
        autoContinueIntervalRef.current = setInterval(() => {
          const remaining = Math.max(0, TOTAL_MS - (Date.now() - startedAt));
          setAutoContinueMs(remaining);
          if (remaining <= 0 && autoContinueIntervalRef.current) {
            clearInterval(autoContinueIntervalRef.current);
            autoContinueIntervalRef.current = null;
          }
        }, 100);
        // Hard-fire auto-advance at 3s.
        // FIXED 2026-06-03: previously called `handleContinue()`, whose
        // closure captured a stale `currentSection`. When the student
        // resumed at section 13, the stale closure read currentSection=0
        // and the auto-advance jumped backwards to section 2 (perceived
        // by the user as "repeats"). Use the locally captured
        // `sectionIndex` directly so the next section is always +1
        // relative to the one that just finished.
        autoContinueTimerRef.current = setTimeout(() => {
          autoContinueTimerRef.current = null;
          if (autoContinueIntervalRef.current) {
            clearInterval(autoContinueIntervalRef.current);
            autoContinueIntervalRef.current = null;
          }
          setAutoContinueMs(null);
          if (teachingStateRef.current === STATE.PAUSED) {
            const nextSection = sectionIndex + 1;
            if (nextSection < sectionsRef.current.length) {
              readSection(nextSection);
            } else {
              finishLesson();
            }
          }
        }, TOTAL_MS);
      }
    } finally {
      readingInProgressRef.current = false;
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // BUTTON HANDLERS
  // ═══════════════════════════════════════════════════════════════

  const handleContinue = async () => {
    cancelAutoContinue();
    const nextSection = currentSection + 1;
    if (nextSection < sectionsRef.current.length) {
      await readSection(nextSection);
    } else {
      await finishLesson();
    }
  };

  // SKIP — jump to next section WITHOUT replaying. Kills current audio
  // immediately, drops the prefetch for the section we're abandoning, and
  // jumps directly to N+1. If we're already on the last section, behaves
  // like Continue and runs the lesson outro.
  const handleSkip = async () => {
    cancelAutoContinue();
    // Hard-stop current playback so we don't double up with the new section.
    killAllAudio();
    if (autoContinueTimerRef.current) {
      clearTimeout(autoContinueTimerRef.current);
      autoContinueTimerRef.current = null;
    }
    readingInProgressRef.current = false;
    teachingRunIdRef.current += 1;
    const target = currentSection + 1;
    if (target < sectionsRef.current.length) {
      await readSection(target);
    } else {
      await finishLesson();
    }
  };

  const handleRepeat = async () => {
    cancelAutoContinue();
    await readSection(currentSection);
  };

  const handleExplain = async () => {
    if (currentSection >= sectionsRef.current.length) return;

    // INTERRUPT PROTOCOL: Immediately kill main lesson audio
    cancelAutoContinue();
    killAllAudio();

    const sectionText = sectionsRef.current[currentSection];
    setTeachingState(STATE.EXPLAINING);
    setHighlightedText(sectionText);
    setStatusMessage("Let me explain that differently...");

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/voice-first/explain`,
        {
          topic: sectionText.substring(0, 500),
          grade_level: gradeLevel,
          is_examination: isExamMode,
          current_paragraph: sectionText,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );

      if (teachingStateRef.current === STATE.IDLE) return;

      const explainText = response.data?.explanation_text || "";
      setExplanationText(explainText);

      if (response.data?.audio_url) {
        await playAudio(explainText, response.data.audio_url);
      } else if (explainText) {
        const audioUrl = await generateAudio(explainText);
        await playAudio(explainText, audioUrl);
      }

      if (teachingStateRef.current !== STATE.IDLE) {
        setTeachingState(STATE.PAUSED);
        setStatusMessage("Click Continue to resume the lesson.");
      }
    } catch (err) {
      // Fallback — simplified read of first sentence
      const simplified = `Let me explain this simply. ${sectionText.split(".")[0]}.`;
      setExplanationText(simplified);
      const audioUrl = await generateAudio(simplified);
      await playAudio(simplified, audioUrl);
      if (teachingStateRef.current !== STATE.IDLE) {
        setTeachingState(STATE.PAUSED);
        setStatusMessage("Click Continue to resume the lesson.");
      }
    }
  };

  const handleAskQuestion = () => {
    // Kill any audio before activating mic
    cancelAutoContinue();
    killAllAudio();
    setError("");  // clear any prior STT/mic error
    setQuestionPanelOpen(true);
    startMicrophone();
  };

  const handleStudentQuestion = async (question) => {
    setStatusMessage(`You asked: "${question}"`);
    setTeachingState(STATE.EXPLAINING);

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${API}/classroom/voice-first/explain`,
        {
          topic: question,
          grade_level: gradeLevel,
          is_examination: isExamMode,
          current_paragraph: sectionsRef.current[currentSection] || "",
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 30000 }
      );

      if (teachingStateRef.current === STATE.IDLE) return;

      const answerText = response.data?.explanation_text || "";
      setExplanationText(answerText);
      // Capture web-search citations (empty array when offline / DDG fails →
      // the model-only answer still renders, just without a Sources block).
      setCitations(Array.isArray(response.data?.citations) ? response.data.citations : []);
      setExpandedCitations(new Set());  // collapse any previous expansions on new answer

      if (response.data?.audio_url) {
        await playAudio(answerText, response.data.audio_url);
      } else if (answerText) {
        const audioUrl = await generateAudio(answerText);
        await playAudio(answerText, audioUrl);
      }

      if (teachingStateRef.current !== STATE.IDLE) {
        setTeachingState(STATE.PAUSED);
        setStatusMessage("Click Continue to resume the lesson.");
      }
    } catch (err) {
      setError("Could not process your question. Please try again.");
      setTeachingState(STATE.PAUSED);
    }
  };

  const handleStopReading = () => {
    // Bump the run ID so any in-flight startTeaching/readSection continuation
    // (e.g., an intro that hasn't finished, or a generateAudio Promise pending)
    // aborts cleanly on its next stale-check instead of overriding PAUSED state
    // back to READING. Without this, a Stop tap during the intro is no-op'd by
    // the auto-continuation into readSection(0).
    teachingRunIdRef.current += 1;
    readingInProgressRef.current = false;
    cancelAutoContinue();
    killAllAudio();
    stopMicrophone();
    // Drop any prefetched audio — when the user clicks Continue we want to
    // re-generate from the current section (which may have changed).
    prefetchRef.current = { index: -1, audioUrl: null, promise: null };
    setTeachingState(STATE.PAUSED);
    setStatusMessage("Paused. Use the controls to continue.");
    setExplanationText("");
  };

  const stopTeaching = () => {
    // Bump the run ID so any in-flight generateAudio / playAudio Promises
    // from the previous run abort cleanly on their next stale-check.
    teachingRunIdRef.current += 1;
    readingInProgressRef.current = false;
    cancelAutoContinue();
    killAllAudio();
    stopMicrophone();
    setTeachingState(STATE.IDLE);
    teachingStateRef.current = STATE.IDLE;
    if (globalComponentId === componentIdRef.current) {
      globalAudioLock = false;
      globalComponentId = null;
      globalAudioInstance = null;
    }
    setStatusMessage("");
    setHighlightedText("");
    setExplanationText("");
    setSttError(false);
    onTeachingEnd?.();
  };

  const finishLesson = async () => {
    setTeachingState(STATE.READING);
    const outro = "Excellent work! You've completed this lesson. May you be blessed in your studies.";
    setStatusMessage("Lesson complete!");
    const outroAudio = await generateAudio(outro);
    await playAudio(outro, outroAudio);
    setTeachingState(STATE.IDLE);
    if (globalComponentId === componentIdRef.current) {
      globalAudioLock = false;
      globalComponentId = null;
    }
    // Lesson fully consumed — clear the resume bookmark so a fresh visit
    // starts from the beginning.
    if (progressStorageKey) {
      try { localStorage.removeItem(progressStorageKey); } catch {}
    }
    // Also clear the global "last lesson" pointer if it matches this lesson
    // (otherwise the Dashboard resume card would keep nagging the student to
    // continue a lesson they just finished).
    try {
      const last = JSON.parse(localStorage.getItem("ylc_last_lesson") || "null");
      if (last && last.lessonKey === lessonKey) {
        localStorage.removeItem("ylc_last_lesson");
      }
    } catch {}
    // Notify parent so the LessonWorkspace can surface the
    // "Next Lesson →" button celebration banner without the student
    // needing to scroll to the bottom.
    onLessonComplete?.();
    onTeachingEnd?.();
  };

  // Derived state
  const isActive = teachingState !== STATE.IDLE;
  const isReading = teachingState === STATE.READING;
  const isPaused = teachingState === STATE.PAUSED;
  const isListening = teachingState === STATE.LISTENING;
  const isExplaining = teachingState === STATE.EXPLAINING;

  // ═══════════════════════════════════════════════════════════════
  // UI
  // ═══════════════════════════════════════════════════════════════

  return (
    <div
      data-testid="invisible-teacher"
      style={{
        position: "fixed",
        bottom: "70px",
        right: "20px",
        zIndex: 9999,
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: "10px",
        maxWidth: "340px",
      }}
    >
      {/* Explanation / Highlight Panel */}
      {(isExplaining || explanationText) && highlightedText && (
        <div
          data-testid="explain-panel"
          style={{
            background: "rgba(0,0,0,0.95)",
            border: "1px solid #FFD700",
            borderRadius: "12px",
            padding: "15px",
            maxWidth: "320px",
            maxHeight: "220px",
            overflowY: "auto",
          }}
        >
          <div
            style={{
              fontSize: "0.7rem",
              color: "#FFD700",
              marginBottom: "8px",
              fontWeight: "bold",
              letterSpacing: "0.5px",
            }}
          >
            CURRENTLY EXPLAINING:
          </div>
          <div
            style={{
              color: "#FFF",
              fontSize: "0.8rem",
              background: "rgba(255,215,0,0.1)",
              padding: "8px",
              borderRadius: "6px",
              borderLeft: "3px solid #FFD700",
              lineHeight: "1.4",
            }}
          >
            {highlightedText.length > 200
              ? highlightedText.substring(0, 200) + "..."
              : highlightedText}
          </div>
          {explanationText && (
            <div
              style={{
                color: "#90EE90",
                fontSize: "0.8rem",
                marginTop: "10px",
                fontStyle: "italic",
                lineHeight: "1.4",
              }}
            >
              {explanationText.length > 300
                ? explanationText.substring(0, 300) + "..."
                : explanationText}
            </div>
          )}
          {/* Live web-search citations (only renders when DuckDuckGo returned
              results for the Q&A — gracefully absent for paragraph-only
              "explain this passage" requests). Tap a row to reveal the
              snippet excerpt that the AI drew from, then tap again to
              collapse. Stops audio/state propagation up to the parent. */}
          {citations && citations.length > 0 && (
            <div
              data-testid="qa-citations"
              style={{
                marginTop: "10px",
                paddingTop: "8px",
                borderTop: "1px solid #FFD70033",
                fontSize: "0.7rem",
              }}
            >
              <div style={{ color: "#FFD700", fontWeight: "bold", marginBottom: "4px" }}>
                Sources · live web
              </div>
              {citations.map((c) => {
                const isOpen = expandedCitations.has(c.id);
                return (
                  <div
                    key={c.id}
                    data-testid={`qa-citation-row-${c.id}`}
                    style={{ marginBottom: "4px" }}
                  >
                    <button
                      type="button"
                      data-testid={`qa-citation-toggle-${c.id}`}
                      aria-expanded={isOpen}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedCitations((prev) => {
                          const next = new Set(prev);
                          if (next.has(c.id)) next.delete(c.id);
                          else next.add(c.id);
                          return next;
                        });
                      }}
                      style={{
                        background: "transparent",
                        border: "none",
                        color: "#aaa",
                        padding: 0,
                        textAlign: "left",
                        cursor: c.snippet ? "pointer" : "default",
                        fontSize: "0.7rem",
                        lineHeight: 1.3,
                        width: "100%",
                      }}
                      title={c.snippet ? "Tap to see excerpt" : ""}
                    >
                      <span style={{ color: "#FFD700", marginRight: "4px" }}>
                        [{c.id}]
                      </span>
                      <a
                        href={c.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          color: "#7ddcff",
                          textDecoration: "none",
                          marginRight: "4px",
                        }}
                      >
                        {c.title}
                      </a>
                      {c.snippet && (
                        <span style={{ color: "#666", fontSize: "0.65rem" }}>
                          {isOpen ? "▾" : "▸"}
                        </span>
                      )}
                    </button>
                    {isOpen && c.snippet && (
                      <div
                        data-testid={`qa-citation-snippet-${c.id}`}
                        style={{
                          marginTop: "3px",
                          marginLeft: "20px",
                          padding: "6px 8px",
                          background: "rgba(255, 215, 0, 0.04)",
                          borderLeft: "2px solid #FFD70060",
                          borderRadius: "4px",
                          color: "#bbb",
                          fontSize: "0.7rem",
                          fontStyle: "italic",
                          lineHeight: 1.45,
                        }}
                      >
                        “{c.snippet}”
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Status Message */}
      {statusMessage && (
        <div
          data-testid="teacher-status"
          style={{
            background: "#0b0b0b",
            color: sttError ? "#FF6B6B" : "#FFD700",
            padding: "10px 15px",
            borderRadius: "10px",
            fontSize: "0.85rem",
            maxWidth: "280px",
            textAlign: "center",
            border: `1px solid ${sttError ? "#FF6B6B" : "#FFD700"}`,
          }}
        >
          {statusMessage}
          {/* 3-second auto-advance countdown bar — shows the student
              how long they have to tap Repeat before the next section
              starts automatically. */}
          {autoContinueMs != null && (
            <div
              data-testid="auto-continue-countdown"
              style={{
                marginTop: "8px",
                height: "3px",
                background: "rgba(255,215,0,0.15)",
                borderRadius: "2px",
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  height: "100%",
                  width: `${Math.max(0, (autoContinueMs / 3000) * 100)}%`,
                  background: "linear-gradient(90deg, #FFD700, #87CEEB)",
                  transition: "width 0.1s linear",
                }}
              />
            </div>
          )}
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div
          data-testid="teacher-error"
          style={{
            background: "rgba(255,0,0,0.2)",
            color: "#ff6b6b",
            padding: "8px 12px",
            borderRadius: "8px",
            fontSize: "0.8rem",
          }}
        >
          {error}
        </div>
      )}

      {/* Control Buttons — single compact row with solid dark backdrop so
          the pills never bleed over the lesson text behind the floating
          panel. Each pill is icon-led with a short label; the main play
          button sits on the right. */}
      <div
        style={{
          display: "flex",
          flexWrap: "nowrap",
          gap: "6px",
          justifyContent: "flex-end",
          alignItems: "center",
          background: "rgba(0,0,0,0.92)",
          border: "1px solid rgba(255,215,0,0.25)",
          borderRadius: "999px",
          padding: "6px 8px",
          boxShadow: "0 8px 24px rgba(0,0,0,0.6)",
          overflowX: "auto",
          maxWidth: "100%",
        }}
      >
        {/* STOP READING — visible during READING or EXPLAINING */}
        {(isReading || isExplaining) && (
          <>
            <button
              data-testid="stop-reading-btn"
              onClick={handleStopReading}
              title="Stop reading"
              style={{
                background: "rgba(255,100,100,0.95)",
                color: "#fff",
                border: "none",
                borderRadius: "999px",
                padding: "8px 12px",
                fontSize: "0.78rem",
                fontWeight: "bold",
                cursor: "pointer",
                whiteSpace: "nowrap",
                flexShrink: 0,
              }}
            >
              &#9632; Stop
            </button>
            {/* SKIP visible while reading — leap to next section without restart */}
            {currentSection < sectionsRef.current.length - 1 && (
              <button
                data-testid="skip-section-btn-reading"
                onClick={handleSkip}
                title={`Skip to section ${currentSection + 2}`}
                style={pillSecondary("#FFD700")}
              >
                &#9197; Skip
              </button>
            )}
          </>
        )}

        {/* CONTINUE — visible when PAUSED (still has sections ahead) */}
        {isPaused && currentSection < sectionsRef.current.length - 1 && (
          <button
            data-testid="continue-btn"
            onClick={handleContinue}
            title="Continue to next section"
            style={{
              background: "linear-gradient(135deg, #90EE90 0%, #32CD32 100%)",
              color: "#000",
              border: "none",
              borderRadius: "999px",
              padding: "8px 14px",
              fontSize: "0.8rem",
              fontWeight: "bold",
              cursor: "pointer",
              whiteSpace: "nowrap",
              flexShrink: 0,
            }}
          >
            &#9654; Continue
          </button>
        )}

        {/* SKIP — visible when PAUSED (jump forward without re-reading) */}
        {isPaused && currentSection < sectionsRef.current.length - 1 && (
          <button
            data-testid="skip-section-btn"
            onClick={handleSkip}
            title={`Skip to section ${currentSection + 2}`}
            style={pillSecondary("#FFD700")}
          >
            &#9197; Skip
          </button>
        )}

        {/* REPEAT — visible when PAUSED. Made extra-prominent during the
            3-second auto-advance countdown so the student sees the "hear
            this section again" option clearly before the next section starts. */}
        {isPaused && (
          <button
            data-testid="repeat-btn"
            onClick={handleRepeat}
            title="Replay this section for clarity"
            style={
              autoContinueMs != null
                ? {
                    background: "linear-gradient(90deg, #87CEEB, #4a90c2)",
                    color: "#000",
                    border: "1px solid #87CEEB",
                    borderRadius: "999px",
                    padding: "8px 14px",
                    fontSize: "0.78rem",
                    fontWeight: "bold",
                    cursor: "pointer",
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    boxShadow: "0 0 12px rgba(135,206,235,0.5)",
                    animation: "itPulse 1.4s ease-in-out infinite",
                  }
                : pillSecondary("#87CEEB")
            }
          >
            &#8635; Repeat
          </button>
        )}

        {/* EXPLAIN — visible when PAUSED & NOT exam mode */}
        {isPaused && !isExamMode && (
          <button
            data-testid="explain-btn"
            onClick={handleExplain}
            title="Explain this section in simpler words"
            style={pillSecondary("#FFD700")}
          >
            ? Explain
          </button>
        )}

        {/* ASK QUESTION — visible when PAUSED (Push-to-Talk) */}
        {isPaused && !isListening && (
          <button
            data-testid="ask-question-btn"
            onClick={handleAskQuestion}
            title="Ask the teacher a question"
            style={pillSecondary("#9370DB")}
          >
            &#127908; Ask
          </button>
        )}

        {/* MAIN CONTROL — Start / Stop Teaching */}
        <button
          data-testid="teacher-main-btn"
          onClick={isActive ? stopTeaching : startTeaching}
          disabled={sections.length === 0}
          style={{
            background: isActive
              ? "rgba(255,100,100,0.9)"
              : "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
            color: isActive ? "#fff" : "#000",
            border: "none",
            borderRadius: "50%",
            width: "44px",
            height: "44px",
            fontSize: "1.1rem",
            cursor: sections.length === 0 ? "not-allowed" : "pointer",
            boxShadow: isActive
              ? "0 4px 16px rgba(255,100,100,0.5)"
              : "0 4px 16px rgba(255,215,0,0.5)",
            transition: "all 0.3s ease",
            animation: isReading || isExplaining ? "itPulse 1.5s infinite" : "none",
            flexShrink: 0,
            marginLeft: "4px",
          }}
          title={isActive ? "Stop Teaching" : currentSection > 0 ? "Resume Teaching" : "Start Teaching"}
        >
          {isReading || isExplaining ? "\u266B" : isActive ? "\u25A0" : "\u25B6"}
        </button>
      </div>

      {/* ═══ QUESTION PANEL: editable text input + mic + Send ═══
          2026-04-25 — User-Friendly Voice Input. Single editable input that
          receives streamed speech-to-text from the browser's Web Speech API.
          NEVER auto-sends — user reviews/edits, then taps Send manually. */}
      {questionPanelOpen && (
        <div
          data-testid="listening-panel"
          style={{
            background: "#0b0b0b",
            border: `2px solid ${isListening ? "#ff4444" : "#FFD70060"}`,
            borderRadius: "12px",
            padding: "15px",
            width: "320px",
            maxWidth: "calc(100vw - 24px)",
            animation: isListening ? "itGlow 2s infinite" : "none",
          }}
        >
          {/* Status row (only while recording) */}
          {isListening && (
            <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "10px" }}>
              <div
                aria-hidden
                style={{
                  width: "12px", height: "12px", borderRadius: "50%",
                  background: "#ff4444", animation: "itPulse 1s infinite",
                }}
              />
              <span style={{ color: "#ff4444", fontSize: "0.8rem", fontWeight: "bold" }}>
                RECORDING — speak naturally
              </span>
            </div>
          )}

          {/* Editable text input + Mic + Send  (the core of the unified UX) */}
          <form
            onSubmit={handleTextSubmit}
            style={{ display: "flex", gap: "6px", alignItems: "stretch", marginBottom: "8px" }}
          >
            <input
              data-testid="question-text-input"
              type="text"
              value={textQuestion}
              onChange={(e) => {
                setTextQuestion(e.target.value);
                // If the user manually edits during recording, take their
                // edit as the new baseline so further speech appends to it.
                if (isListening) {
                  textAtRecordingStartRef.current = e.target.value;
                  finalTranscriptRef.current = "";
                  setFinalTranscript("");
                }
              }}
              placeholder={
                isListening ? "Listening… speak now" : "Type or tap 🎤 to speak"
              }
              autoFocus
              style={{
                flex: 1,
                padding: "10px 12px",
                borderRadius: "10px",
                border: `1px solid ${isListening ? "#ff4444" : "#555"}`,
                background: "#1a1a1a",
                color: "#fff",
                fontSize: "0.9rem",
                outline: "none",
                minWidth: 0,
              }}
            />
            <button
              type="button"
              data-testid="mic-toggle-btn"
              aria-label={isListening ? "Stop recording" : "Start recording"}
              onClick={toggleMicrophone}
              style={{
                background: isListening
                  ? "linear-gradient(135deg, #ff4444 0%, #cc0000 100%)"
                  : "linear-gradient(135deg, #FFD700 0%, #DAA520 100%)",
                color: isListening ? "#fff" : "#000",
                border: "none",
                borderRadius: "10px",
                width: "44px",
                fontSize: "1.1rem",
                fontWeight: "bold",
                cursor: "pointer",
                flexShrink: 0,
                animation: isListening ? "itPulse 1s infinite" : "none",
                width: "auto",
                marginTop: 0,
              }}
              title={isListening ? "Stop recording" : "Start voice input"}
            >
              {isListening ? "■" : "🎤"}
            </button>
            <button
              type="submit"
              data-testid="send-question-btn"
              disabled={!textQuestion.trim()}
              style={{
                background: textQuestion.trim()
                  ? "linear-gradient(135deg, #90EE90 0%, #32CD32 100%)"
                  : "#2a2a2a",
                color: textQuestion.trim() ? "#000" : "#666",
                border: "none",
                borderRadius: "10px",
                padding: "0 14px",
                fontSize: "0.85rem",
                fontWeight: "bold",
                cursor: textQuestion.trim() ? "pointer" : "not-allowed",
                flex: "0 0 auto",
                width: "auto",
                marginTop: 0,
              }}
              title="Send your question to the Invisible Teacher"
            >
              Send
            </button>
          </form>

          {/* Live interim preview (faint, under the input — non-editable hint) */}
          {isListening && interimTranscript && (
            <div
              data-testid="interim-preview"
              style={{
                color: "#888",
                fontSize: "0.75rem",
                fontStyle: "italic",
                marginBottom: "6px",
                paddingLeft: "4px",
              }}
            >
              … {interimTranscript}
            </div>
          )}

          {/* Cancel — closes panel + clears text */}
          <button
            data-testid="cancel-question-btn"
            onClick={cancelListening}
            style={{
              background: "transparent",
              color: "#ff8c8c",
              border: "1px solid #ff6b6b60",
              borderRadius: "8px",
              padding: "5px 12px",
              fontSize: "0.75rem",
              cursor: "pointer",
              width: "100%",
            }}
          >
            Cancel
          </button>
        </div>
      )}

      {/* Progress Indicator */}
      {isActive && sections.length > 0 && (
        <div
          data-testid="teacher-progress"
          style={{
            background: "rgba(0,0,0,0.8)",
            borderRadius: "10px",
            padding: "8px 15px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          <div
            style={{
              width: "100px",
              height: "6px",
              background: "#333",
              borderRadius: "3px",
              overflow: "hidden",
            }}
          >
            <div
              style={{
                width: `${((currentSection + 1) / sections.length) * 100}%`,
                height: "100%",
                background: "#FFD700",
                transition: "width 0.3s ease",
              }}
            />
          </div>
          <span style={{ color: "#ccc", fontSize: "0.75rem" }}>
            {currentSection + 1}/{sections.length}
          </span>
        </div>
      )}

      {/* Listening Indicator (red dot) */}
      {isListening && (
        <div
          data-testid="listening-indicator"
          style={{
            position: "absolute",
            top: "-10px",
            right: "-10px",
            background: "#ff4444",
            borderRadius: "50%",
            width: "20px",
            height: "20px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            animation: "itPulse 1s infinite",
            fontSize: "0.6rem",
            color: "#fff",
          }}
        >
          &#9679;
        </div>
      )}

      <style>
        {`
          @keyframes itGlow {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,68,68,0.5); }
            50% { box-shadow: 0 0 18px 4px rgba(255,68,68,0.45); }
          }
          @keyframes itPulse {
            0%, 100% { transform: scale(1); opacity: 1; }
            50% { transform: scale(1.1); opacity: 0.8; }
          }
        `}
      </style>
    </div>
  );
}

// Named exports for flexibility
export { InvisibleTeacher, getVoice, VOICES };
